/**
 * Aspect — Decor (Unity curtains on windows)
 * --------------------------------------------------------------
 * Places Apartment Modern curtain GLBs on dry-room windows. Plants / wall art
 * stay off — those come from the placeable catalog.
 */

import * as pc from "playcanvas";
import { wallLength, wallPointAt, wallTangentAt, paramOnWall } from "./wall_geometry.js";
import { hasOpeningAsset, initOpeningAssets, instantiateOpening, OPENING_NATIVE } from "./opening_assets.js";
import { isTouchUi } from "../device_profile.js";

const CURTAIN_COLORS = [
  [0.82, 0.79, 0.72], // warm linen
  [0.66, 0.7, 0.72], // cool grey
];
const ART_COLORS = [
  [0.72, 0.5, 0.38],
  [0.36, 0.48, 0.55],
  [0.62, 0.6, 0.5],
  [0.45, 0.42, 0.52],
];

const isWetType = (t) => /(bath|wc|toilet|shower|laundry|wet)/i.test(String(t ?? ""));
const isLivingType = (t) => /(living|lounge|salon|family)/i.test(String(t ?? ""));
const isBedType = (t) => /(bed|master|guest)/i.test(String(t ?? ""));

function mat(diffuse, { gloss = 0.25, metalness = 0, emissive = null, emissiveIntensity = 0 } = {}) {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(diffuse[0], diffuse[1], diffuse[2]);
  m.gloss = gloss;
  m.useMetalness = true;
  m.metalness = metalness;
  if (emissive) {
    m.emissive = new pc.Color(emissive[0], emissive[1], emissive[2]);
    m.emissiveIntensity = emissiveIntensity;
  }
  m.update();
  return m;
}

/** Lay a Y-axis cylinder primitive along the wall tangent (horizontal rod). */
function orientCylinderAlongTangent(entity, tx, tz) {
  const alongZ = Math.abs(tx) < 1e-3;
  if (alongZ) {
    entity.setLocalEulerAngles(90, 0, 0);
  } else if (Math.abs(tz) < 1e-3) {
    entity.setLocalEulerAngles(0, 0, 90);
  } else {
    const yaw = (Math.atan2(-tz, tx) * 180) / Math.PI;
    entity.setLocalEulerAngles(0, yaw, 90);
  }
}

/** Face a thin box (curtain panel) toward the room interior. */
function orientPanelFacingInterior(entity, nx, nz) {
  entity.setLocalEulerAngles(0, Math.atan2(nx, nz) * pc.math.RAD_TO_DEG, 0);
}

function prim(name, type, material, scale, pos, { castShadows = true } = {}) {
  const e = new pc.Entity(name);
  e.addComponent("render", { type, material, castShadows, receiveShadows: true });
  e.setLocalScale(scale[0], scale[1], scale[2]);
  e.setLocalPosition(pos[0], pos[1], pos[2]);
  e._aspectInteractable = false;
  return e;
}

/** Deterministic per-room pseudo-random from a string id (stable across reloads). */
function seededPick(seed, arr) {
  let h = 0;
  const s = String(seed);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return arr[h % arr.length];
}

export class DecorSystem {
  /**
   * @param {pc.AppBase} app
   * @param {import('../scene_manager.js').SceneManager} scene
   */
  constructor(app, scene) {
    this.app = app;
    this.scene = scene;
    this.root = null;
    this.enabled = true;

    this._onRoom = (room) => this.rebuild(room);
    this._onGeometry = () => this.rebuild(this.scene.room);
    app.on("scene:room-loaded", this._onRoom);
    app.on("scene:geometry-changed", this._onGeometry);
    app.on("ui:toggle-decor", ({ on }) => {
      this.enabled = on !== false;
      this.rebuild(this.scene.room);
    });

    // Prefetch Unity curtain GLB; rebuild once it arrives. Phones skip — they
    // only load the Bedroom and must not pull extra opening meshes.
    if (scene?.registry && !isTouchUi()) {
      initOpeningAssets(scene.registry).then(() => this.rebuild(this.scene.room));
    }
  }

  rebuild(room) {
    this.root?.destroy();
    this.root = null;
    if (!this.enabled || !room || !this.scene.roomRoot) {
      this.app.renderNextFrame = true;
      return;
    }

    const root = new pc.Entity("decor-root");
    this.scene.roomRoot.addChild(root);
    this.root = root;

    const floorY = room.floorY ?? 0;
    const ceilH = room.dimensions?.height ?? 2.8;
    const b = room.bounds;
    const center = b?.min && b?.max
      ? [(b.min[0] + b.max[0]) / 2, (b.min[2] + b.max[2]) / 2]
      : [0, 0];
    const mats = {
      rod: mat([0.35, 0.35, 0.38], { gloss: 0.55, metalness: 0.6 }),
    };
    // Unity curtain meshes only (plants / wall art stay off — catalog furniture).
    this._curtains(room, root, mats, floorY, ceilH, center);
    this.app.renderNextFrame = true;
  }

  /** Unity curtain GLB per dry-room window (procedural rod/panels as fallback). */
  _curtains(room, root, mats, floorY, ceilH, center) {
    const wetRects = (room.rooms ?? [])
      .filter((r) => isWetType(r.type))
      .map((r) => ({
        x0: room.bounds.min[0] + (Number(r.x) || 0),
        z0: room.bounds.min[2] + (Number(r.z) || 0),
        w: Number(r.width) || 0,
        d: Number(r.depth) || 0,
      }));
    const inWet = (x, z) => wetRects.some((r) => x >= r.x0 - 0.3 && x <= r.x0 + r.w + 0.3 && z >= r.z0 - 0.3 && z <= r.z0 + r.d + 0.3);

    const useGlb = hasOpeningAsset("curtain");
    const nat = OPENING_NATIVE.curtain;
    let ci = 0;
    for (const op of room.openings ?? []) {
      if (op.type !== "window" || !Array.isArray(op.center)) continue;
      if (op.curtain === false) continue;
      const wall = (room.walls ?? []).find((w) => w.id === op.wall);
      if (!wall) continue;
      const wx = op.center[0];
      const wz = op.center[2];
      if (inWet(wx, wz)) continue;

      const t = paramOnWall(wall, wx, wz);
      const [tx, tz] = wallTangentAt(wall, t);
      // Interior side: normal pointing toward the envelope center.
      let nx = -tz;
      let nz = tx;
      if ((center[0] - wx) * nx + (center[1] - wz) * nz < 0) {
        nx = -nx;
        nz = -nz;
      }

      const opW = Number(op.width) || 1.2;
      const sill = op.sill != null ? Number(op.sill) : 0.9;
      const opH = Number(op.height) || 1.2;
      // Keep the curtain clear of the window frame (native depth ~0.37 m).
      const inset = 0.28;

      if (useGlb) {
        const mesh = instantiateOpening("curtain");
        if (mesh) {
          // Width is on local Z — align Z with the wall, then flip so +X faces the room.
          let yaw = (Math.atan2(tx, tz) * 180) / Math.PI;
          const xAlongNx = tz;
          const xAlongNz = -tx;
          if (xAlongNx * nx + xAlongNz * nz < 0) yaw += 180;
          // Rod sits just above the window head; fabric hangs to the floor.
          const windowTop = floorY + sill + opH;
          const topY = Math.min(floorY + ceilH - 0.04, windowTop + 0.1);
          const bottomY = floorY;
          const panelH = Math.max(1.2, topY - bottomY);
          // Park the panel on one side of the opening so it doesn't block the view.
          const side = ci % 2 === 0 ? -1 : 1;
          const along = side * Math.max(0, (opW - nat.width * 0.55) / 2);
          mesh.setLocalEulerAngles(0, yaw, 0);
          mesh.setLocalScale(
            0.85,
            panelH / nat.height,
            Math.min(1.15, Math.max(0.55, (opW * 0.55) / nat.width)),
          );
          mesh.setLocalPosition(
            wx + nx * inset + tx * along,
            bottomY,
            wz + nz * inset + tz * along,
          );
          mesh.name = `curtain-${op.id}`;
          mesh._aspectKind = "curtain";
          mesh._aspectOpeningId = op.id;
          mesh._aspectInteractable = true;
          root.addChild(mesh);
          ci++;
          continue;
        }
      }

      const rodY = Math.min(floorY + ceilH - 0.08, floorY + sill + opH + 0.14);
      const rod = prim(
        `curtain-rod-${op.id}`,
        "cylinder",
        mats.rod,
        [0.025, opW + 0.5, 0.025],
        [wx + nx * inset, rodY, wz + nz * inset],
        { castShadows: false },
      );
      orientCylinderAlongTangent(rod, tx, tz);
      rod._aspectKind = "curtain";
      rod._aspectOpeningId = op.id;
      rod._aspectInteractable = true;
      root.addChild(rod);

      const color = seededPick(op.id ?? ci, CURTAIN_COLORS);
      const fabric = mat(color, { gloss: 0.08 });
      const panelW = Math.max(0.35, opW * 0.32);
      const panelH = rodY - floorY - 0.03;
      for (const side of [-1, 1]) {
        const off = side * (opW / 2 + panelW * 0.28);
        const panel = prim(
          `curtain-${op.id}-${side > 0 ? "r" : "l"}`,
          "box",
          fabric,
          [panelW, panelH, 0.06],
          [wx + tx * off + nx * inset, rodY - panelH / 2 - 0.02, wz + tz * off + nz * inset],
          { castShadows: false },
        );
        orientPanelFacingInterior(panel, nx, nz);
        panel._aspectKind = "curtain";
        panel._aspectOpeningId = op.id;
        panel._aspectInteractable = true;
        root.addChild(panel);
      }
      ci++;
    }
  }

  /** Plants in living-room corners, framed art near living/bedroom centroids. */
  _roomDecor(room, root, mats, floorY, b) {
    if (!b) return;
    let artCount = 0;
    for (const r of room.rooms ?? []) {
      const rw = Number(r.width) || 0;
      const rd = Number(r.depth) || 0;
      if (rw < 1.2 || rd < 1.2) continue;
      const x0 = b.min[0] + (Number(r.x) || 0);
      const z0 = b.min[2] + (Number(r.z) || 0);
      const cx = x0 + rw / 2;
      const cz = z0 + rd / 2;

      if (isLivingType(r.type)) {
        this._plant(root, mats, `plant-${r.name ?? cx.toFixed(1)}`, x0 + 0.45, floorY, z0 + 0.45);
      }
      if ((isLivingType(r.type) || isBedType(r.type)) && artCount < 4) {
        if (this._art(room, root, mats, `art-${r.name ?? artCount}`, cx, cz, floorY)) artCount++;
      }
    }
  }

  /** Potted plant: pot + trunk + three foliage blobs. */
  _plant(root, mats, name, x, floorY, z) {
    const g = new pc.Entity(name);
    g.setLocalPosition(x, floorY, z);
    g.addChild(prim(`${name}-pot`, "cylinder", mats.pot, [0.34, 0.3, 0.34], [0, 0.15, 0]));
    g.addChild(prim(`${name}-trunk`, "cylinder", mats.trunk, [0.05, 0.5, 0.05], [0, 0.5, 0]));
    g.addChild(prim(`${name}-leaf-a`, "sphere", mats.leaf, [0.55, 0.5, 0.55], [0, 0.95, 0]));
    g.addChild(prim(`${name}-leaf-b`, "sphere", mats.leaf, [0.4, 0.38, 0.4], [0.18, 1.2, 0.1]));
    g.addChild(prim(`${name}-leaf-c`, "sphere", mats.leaf, [0.34, 0.3, 0.34], [-0.16, 1.15, -0.08]));
    root.addChild(g);
  }

  /**
   * Framed canvas on the wall nearest (cx, cz), skipping spots near openings.
   * @returns {boolean} placed
   */
  _art(room, root, mats, name, cx, cz, floorY) {
    let best = null;
    for (const w of room.walls ?? []) {
      if (wallLength(w) < 1.4) continue;
      const t = paramOnWall(w, cx, cz);
      const [px, pz] = wallPointAt(w, t);
      const d = Math.hypot(px - cx, pz - cz);
      if (!best || d < best.d) best = { w, t, px, pz, d };
    }
    if (!best || best.d > 6) return false;

    // Reject spots that collide with a door/window on that wall.
    const ART_W = 0.9;
    for (const op of room.openings ?? []) {
      if (op.wall !== best.w.id || !Array.isArray(op.center)) continue;
      const dAlong = Math.abs(
        paramOnWall(best.w, op.center[0], op.center[2]) * wallLength(best.w) - best.t * wallLength(best.w),
      );
      if (dAlong < (Number(op.width) || 1) / 2 + ART_W / 2 + 0.25) return false;
    }

    const [tx, tz] = wallTangentAt(best.w, best.t);
    let nx = -tz;
    let nz = tx;
    if ((cx - best.px) * nx + (cz - best.pz) * nz < 0) {
      nx = -nx;
      nz = -nz;
    }
    const yaw = Math.atan2(nx, nz) * pc.math.RAD_TO_DEG;
    const inset = 0.09;
    const y = floorY + 1.55;
    const px = best.px + nx * inset;
    const pz = best.pz + nz * inset;

    const frame = prim(`${name}-frame`, "box", mats.frame, [ART_W, 0.68, 0.035], [px, y, pz], { castShadows: false });
    frame.setLocalEulerAngles(0, yaw, 0);
    root.addChild(frame);

    const canvasMat = mat(seededPick(name, ART_COLORS), { gloss: 0.12 });
    const canvas = prim(
      `${name}-canvas`,
      "box",
      canvasMat,
      [ART_W - 0.08, 0.6, 0.04],
      [px + nx * 0.004, y, pz + nz * 0.004],
      { castShadows: false },
    );
    canvas.setLocalEulerAngles(0, yaw, 0);
    root.addChild(canvas);
    return true;
  }

  destroy() {
    this.app.off("scene:room-loaded", this._onRoom);
    this.app.off("scene:geometry-changed", this._onGeometry);
    this.root?.destroy();
    this.root = null;
  }
}

export default DecorSystem;
